import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Radio } from '@mui/material';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import type { Chat } from '@trinity/chat';
import type { HcpSearchResult } from '@trinity/chat';
import type { KolSearchResult } from '@trinity/chat';
import type { LocationItem } from '@trinity/chat';
import { useBrowserLocation } from '@trinity/chat-components/hooks/useBrowserLocation';
import { BrowserLocationError } from './BrowserLocationError';
import { SmartRoutingHcpContext } from './context';
import { SelectHcp } from './KolSearch.Form';
import type { SmartRoutingAction } from './SmartRouting.Container';

type Props = {
  value: Chat.QueryState.SmartRouting;
  hcp: KolSearchResult | HcpSearchResult | null;
  disabled: boolean;
  dispatch: React.Dispatch<SmartRoutingAction>;
  onLocationSubmitted?: (startingLocation: Chat.QueryState.SmartRouting['startingLocation']) => Promise<void>;
};

export const SmartRoutingLocation = (props: Props) => {
  const { fetchedHcp } = useContext(SmartRoutingHcpContext);

  // Determine if we're using HCP search or KOL search
  const isHcpSearch = !!props.value.searchIndex;

  const hcpFromContext = useMemo((): KolSearchResult | HcpSearchResult | null => {
    // Check if HCP passed in from container
    if (props.hcp) {
      return props.hcp;
    }
    // Check if we have kolProfile from state (KOL workflow)
    if (props.value.kolProfile) {
      return props.value.kolProfile as KolSearchResult;
    }
    // Check if we have fetchedHcp from HCP workflow
    if (props.value.hcpId && fetchedHcp) {
      return fetchedHcp as HcpSearchResult;
    }
    return null;
  }, [props.value, props.hcp, fetchedHcp]);

  const [radioState, setRadioState] = useState<'browser' | 'search' | 'hcp'>(hcpFromContext ? 'hcp' : props.value?.startingLocation?.type);
  const [selectorState, setSelectorState] = useState<LocationItem | null>(props.value?.startingLocation?.type === 'search' ? props.value.startingLocation as LocationItem : null);
  const [browserLocation, setBrowserLocation] = useState<ICoords | null>(props.value?.startingLocation?.type === 'browser' ? props.value.startingLocation as ICoords : null);
  const [selectedHcp, setSelectedHcp] = useState<KolSearchResult | HcpSearchResult | null>(hcpFromContext);
  const [hcpInputValue, setHcpInputValue] = useState<string>(hcpFromContext?.name || '');
  const [locationError, setLocationError] = useState<string | null>(null);
  const { isSupported, getLocation, state: browserLocationState } = useBrowserLocation({ onSuccess: setBrowserLocation, onError: setLocationError });

  useEffect(() => {
    if (radioState === 'browser' && !browserLocation) {
      getLocation();
    }
  }, [browserLocation, getLocation, radioState]);

  useEffect(() => {
    if (hcpFromContext && !selectedHcp) {
      setSelectedHcp(hcpFromContext);
      setHcpInputValue(hcpFromContext.name);
      setRadioState('hcp');
    }
  }, [hcpFromContext, selectedHcp]);

  const onSubmit = useCallback(async () => {
    let startingLocation: Chat.QueryState.SmartRouting['startingLocation'] = null;

    if (radioState === 'browser' && browserLocation) {
      startingLocation = {
        type: 'browser',
        latitude: browserLocation.latitude,
        longitude: browserLocation.longitude,
      };
    } else if (radioState === 'search' && selectorState) {
      startingLocation = {
        type: 'search',
        name: selectorState.name,
        latitude: selectorState.latitude,
        longitude: selectorState.longitude,
        id: selectorState.id,
      };
    } else if (radioState === 'hcp' && selectedHcp) {

      let id: number;
      let latitude: number;
      let longitude: number;

      if ('id' in selectedHcp && selectedHcp.id !== undefined) {
        // KolSearchResult
        id = selectedHcp.id;
        latitude = selectedHcp.locationCoords.latitude;
        longitude = selectedHcp.locationCoords.longitude;
      }
      else if ('hcpIdentifier' in selectedHcp) {
        // HcpSearchResult
        const hcp = selectedHcp;
        id = parseInt(hcp.hcpIdentifier, 10);
        if (hcp.primaryLocation?.lat && hcp.primaryLocation?.lon) {
          longitude = hcp.primaryLocation.lon;
          latitude = hcp.primaryLocation.lat;
        }
      }
      startingLocation = {
        type: 'hcp',
        id,
        name: selectedHcp.name,
        latitude,
        longitude,
      };
    }

    if (startingLocation) {
      props.dispatch({
        type: 'submit-location',
        payload: { startingLocation },
      });
      if (props.onLocationSubmitted) {
        await props.onLocationSubmitted(startingLocation);
      }
    }
  }, [browserLocation, props, radioState, selectedHcp, selectorState]);

  const inputFocused = useRef(false);
  const handleHcpInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleHcpInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const canSubmit = useMemo(() => {
    if (props.disabled) return false;
    if (radioState === 'browser' && browserLocation) return true;

    if (radioState === 'search' && selectorState) return true;

    if (radioState === 'hcp' && selectedHcp) return true;

    return false;
  }, [browserLocation, props.disabled, radioState, selectedHcp, selectorState]);

  const apiClient = useChatApiClient();

  return (
    <div>
      <Copy>Welcome to the Smart Routing Assistant! I can help you plan your HCP site visits efficiently.</Copy>
      <Copy>First let's plan our where you'll be starting from.</Copy>
      <List>
        <RadioRow>
          <Radio size='small' disabled={!isSupported || props.disabled} checked={radioState === 'browser'} onClick={() => setRadioState('browser')} />
          <div>Use my current location</div>
          {browserLocationState === 'loading' && <div>Waiting for permission...</div>}
          {browserLocationState === 'success' && <div>{browserLocation.longitude}, {browserLocation.latitude}</div>}
          {browserLocationState === 'error' && <BrowserLocationError errorMessage={locationError} />}
        </RadioRow>
        <RadioRow>
          <Radio size='small' checked={radioState === 'search'} disabled={props.disabled} onClick={() => setRadioState('search')} />
          <RadioCopy>I'll be near this location</RadioCopy>
          <SelectLocation
            styles={{
              container: (provided) => ({
                ...provided,
                width: 350,
              }),
            }}
            placeholder="Start typing an address"
            isDisabled={radioState !== 'search' || props.disabled}
            value={selectorState}
            onChange={setSelectorState} />
        </RadioRow>
        <RadioRow>
          <Radio size='small' checked={radioState === 'hcp'} disabled={props.disabled} onClick={() => setRadioState('hcp')} />
          <RadioCopy>Use HCP's location</RadioCopy>
          <HcpSelectContainer>
            {isHcpSearch ? (
              <SelectHcp<HcpSearchResult>
                disabled={props.disabled || radioState !== 'hcp' || !!hcpFromContext}
                formatOptionLabel={formatHcpOptionLabelForHcp}
                onInputFocus={handleHcpInputFocus}
                onInputBlur={handleHcpInputBlur}
                onInputChange={setHcpInputValue}
                onSelection={(v) => {
                  setSelectedHcp(v);
                  if (!v) {
                    setHcpInputValue('');
                  }
                }}
                isClearable={true}
                value={(selectedHcp as HcpSearchResult | null) ?? null}
                inputValue={selectedHcp ? undefined : hcpInputValue}
                queryFn={apiClient.search.searchHcpsByName}
                searchIndex={props.value.searchIndex}
                getOptionLabel={(option: HcpSearchResult) => option.name}
                getOptionValue={(option: HcpSearchResult) => option.hcpIdentifier}
              />
            ) : (
              <SelectHcp<KolSearchResult>
                disabled={props.disabled || radioState !== 'hcp' || !!hcpFromContext}
                formatOptionLabel={formatHcpOptionLabel}
                onInputFocus={handleHcpInputFocus}
                onInputBlur={handleHcpInputBlur}
                onInputChange={setHcpInputValue}
                onSelection={(v) => {
                  setSelectedHcp(v);
                  if (!v) {
                    setHcpInputValue('');
                  }
                }}
                isClearable={true}
                value={selectedHcp as KolSearchResult | null}
                inputValue={selectedHcp ? undefined : hcpInputValue}
              />
            )}
          </HcpSelectContainer>
        </RadioRow>
      </List>
      <StyledSearchHcps disabled={!canSubmit} onClick={onSubmit} value="Search HCPs" />
    </div>
  );
};

function formatHcpOptionLabel(result: KolSearchResult) {
  const specialty = result.specialties?.[0];
  return (
    <div>
      {result.name}{specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {result.locationName ? <KolResultSecondaryText>{result.locationName}</KolResultSecondaryText> : null}
    </div>
  );
}

function formatHcpOptionLabelForHcp(result: HcpSearchResult) {
  const specialty = result.specialty?.trim();
  const affiliation = result.primaryAffiliationName && result.primaryAffiliationName !== 'N'
    ? result.primaryAffiliationName.trim()
    : null;
  return (
    <div>
      {result.name}{specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {affiliation ? <KolResultSecondaryText>{affiliation}</KolResultSecondaryText> : null}
    </div>
  );
}

const KolResultSecondaryText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  marginTop: 5,
  fontSize: 15,
}));
const KolOptionSubText = styled.span(({ theme }) => ({
  fontSize: 14,
  marginLeft: 5,
  color: theme.palette.gray.main,
}));


const RadioCopy = styled.div(({
  width: 165,
}));

const Copy = styled.div({
  marginBottom: 5,
});

const StyledSearchHcps = styled(SearchQueryHint)({
  marginTop: 10,
});

const List = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const RadioRow = styled.div(() => ({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
}));

const HcpSelectContainer = styled.div({
  width: 350,
});
